import { defineStore } from 'pinia'
import { ref, computed, h } from 'vue'
import { ElMessage, ElNotification } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { getIMWebSocket, destroyIMWebSocket } from '@/utils/websocket'
import * as chatApi from '@/api/chat'
import { getUserList, getAppUserList } from '@/api/user'
import router from '@/router'
import { fetchAndCacheAvatar, restoreAvatar, removeAvatarCache, getImageCacheKey, fetchAndCacheImage, restoreImage } from '@/utils/avatarCache'

// ========== 数据映射常量 ==========

// 会话类型：后端 type+sourceType → 前端字符串
function mapConversationType(conv) {
  if (conv.type === 2) return 'group'
  if (conv.sourceType === 2) return 'customer'
  return 'internal'
}

// 消息类型：后端 msgType → 前端字符串
const MSG_TYPE_MAP = { 1: 'text', 2: 'image', 3: 'file', 10: 'system', 11: 'revoke' }
const MSG_TYPE_REVERSE = { text: 1, image: 2, file: 3, system: 10 }

function mapMsgType(backendType) {
  return MSG_TYPE_MAP[backendType] || 'text'
}

// 成员角色：前端字符串 → 后端数字
const ROLE_REVERSE = { member: 0, admin: 1, owner: 2 }

// 规范化时间字符串，处理后端常见格式
function normalizeTimeStr(val) {
  if (!val) return ''
  // 数字时间戳
  if (typeof val === 'number') return new Date(val).toISOString()
  // 字符串：将 "2026-02-26 09:36:43" 格式转为 ISO "2026-02-26T09:36:43"
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(val)) {
    return val.replace(' ', 'T')
  }
  return val
}

// ========== 数据转换函数 ==========

/**
 * ConversationDTO → 前端会话对象
 */
function transformConversation(apiConv) {
  const frontendType = mapConversationType(apiConv)
  // 提取成员列表（如果后端返回了）
  const members = (apiConv.members || []).map(m => ({
    id: m.userId || m.id,
    name: m.nickname || m.username || m.name || '',
    avatar: m.avatar || m.avatarUrl || '',
    avatarKey: m.avatarKey || '',
    role: m.role === 2 ? 'owner' : m.role === 1 ? 'admin' : 'member',
    department: m.department || m.deptName || '',
    position: m.position || m.postNames || '',
    phone: m.phone || '',
    email: m.email || '',
    userType: m.userType || 1 // 用户类型: 1-平台端用户 2-APP端用户
  }))
  return {
    id: apiConv.id,
    type: frontendType,
    name: apiConv.name || '',
    avatar: apiConv.avatar || '',
    lastMessage: apiConv.lastMsgContent || '',
    lastMessageTime: normalizeTimeStr(apiConv.lastMsgTime),
    unreadCount: apiConv.unreadCount || 0,
    lastMsgSenderId: apiConv.lastMsgSenderId || apiConv.lastSenderId || null,
    online: false,
    department: '',
    position: '',
    phone: '',
    email: '',
    source: '',
    customerLevel: '',
    memberCount: apiConv.memberCount || members.length || 0,
    description: apiConv.notice || '',
    ownerId: apiConv.ownerId || null,
    members,
    pinned: apiConv.pinned || false,
    muted: apiConv.muted || false,
    _raw: {
      type: apiConv.type,
      sourceType: apiConv.sourceType,
      ownerId: apiConv.ownerId
    }
  }
}

/**
 * ImMessageDTO → 前端消息对象
 */
function transformMessage(apiMsg, currentUserId) {
  const type = mapMsgType(apiMsg.msgType)
  // 平台端 senderType=1，APP端 senderType=2
  const isSelf = apiMsg.senderId != null && String(apiMsg.senderId) === String(currentUserId) && apiMsg.senderType === 1
  const result = {
    id: apiMsg.id || apiMsg.messageId,
    senderId: apiMsg.senderId,
    senderType: apiMsg.senderType,
    senderName: isSelf ? '我' : (apiMsg.senderName || apiMsg.senderNickname || apiMsg.nickname || apiMsg.fromNickname || ''),
    content: apiMsg.content || '',
    time: normalizeTimeStr(apiMsg.createTime || apiMsg.sendTime || apiMsg.createdAt),
    type,
    status: isSelf ? 'delivered' : 'read'
  }

  // 撤回消息
  if (apiMsg.revoked) {
    result.type = 'system'
    result.content = `${result.senderName} 撤回了一条消息`
    return result
  }

  // 文件消息：解析 extra
  if (type === 'file' && apiMsg.extra) {
    try {
      const extra = typeof apiMsg.extra === 'string' ? JSON.parse(apiMsg.extra) : apiMsg.extra
      result.fileName = apiMsg.content || extra.fileName || ''
      result.fileSize = formatFileSize(extra.fileSize)
      result.fileUrl = extra.fileUrl || ''
    } catch (e) {
      result.fileName = apiMsg.content || ''
      result.fileSize = ''
    }
  }

  // 图片消息：解析 extra
  if (type === 'image' && apiMsg.extra) {
    try {
      const extra = typeof apiMsg.extra === 'string' ? JSON.parse(apiMsg.extra) : apiMsg.extra
      result.imageUrl = extra.fileUrl || ''
    } catch (e) {
      result.imageUrl = ''
    }
  }

  // 产品卡片消息：解析 extra 中的产品信息
  if (type === 'text' && apiMsg.extra) {
    try {
      const extra = typeof apiMsg.extra === 'string' ? JSON.parse(apiMsg.extra) : apiMsg.extra
      if (extra && extra.type === 'product') {
        result.type = 'product'
        result.productId = extra.productId
        result.productName = extra.name || ''
        result.productIcon = extra.icon || ''
        result.productSummary = extra.summary || ''
      }
    } catch (e) {
      // ignore
    }
  }

  // 引用回复
  if (apiMsg.replyMsgId && apiMsg.quotedMessage) {
    const q = apiMsg.quotedMessage
    if (q.revoked) {
      result.replyTo = { id: q.id, senderName: q.senderName || '', content: '该消息已撤回' }
    } else {
      result.replyTo = { id: q.id, senderName: q.senderName || '', content: q.content || '' }
    }
  } else if (apiMsg.replyMsgId && !apiMsg.quotedMessage) {
    result.replyTo = { id: apiMsg.replyMsgId, senderName: '', content: '原消息已删除' }
  }

  return result
}



/**
 * 格式化文件大小
 */
function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + 'B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + 'KB'
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB'
}

/**
 * 格式化最后一条消息摘要
 */
function formatLastMessage(msg) {
  if (!msg) return ''
  if (msg.type === 'text') return msg.content || ''
  if (msg.type === 'file') return `[文件] ${msg.fileName || ''}`
  if (msg.type === 'image') return '[图片]'
  if (msg.type === 'product') return `[产品] ${msg.productName || ''}`
  if (msg.type === 'system') return msg.content || ''
  return ''
}

// ========== Store 定义 ==========

export const useChatStore = defineStore('chat', () => {
  // ========== 本地缓存 ==========
  const STORAGE_KEY_CONVS = 'im_conversations'
  const STORAGE_KEY_MSGS = 'im_messages'
  const STORAGE_KEY_USERS = 'im_user_cache'

  let _saveTimer = null

  function _saveToStorage() {
    if (_saveTimer) clearTimeout(_saveTimer)
    _saveTimer = setTimeout(() => {
      try {
        // 裁剪 readBy：只保留每个会话最近 200 条消息的 readBy
        const trimmedMsgs = {}
        for (const [convId, msgList] of Object.entries(messages.value)) {
          const keepFrom = Math.max(0, msgList.length - 200)
          trimmedMsgs[convId] = msgList.map((msg, idx) => {
            if (idx < keepFrom && msg.readBy) {
              // eslint-disable-next-line no-unused-vars
              const { readBy, ...rest } = msg
              return rest
            }
            return msg
          })
        }
        localStorage.setItem(STORAGE_KEY_CONVS, JSON.stringify(conversations.value))
        localStorage.setItem(STORAGE_KEY_MSGS, JSON.stringify(trimmedMsgs))
        // 持久化 userCache：剥离 cachedAvatar（blob URL 不可跨 session 持久化）
        // 刷新后通过 _restoreAllAvatarCache 从 IndexedDB 恢复 blob URL，避免用原始 URL 触发 HTTP 请求
        const cacheToSave = {}
        for (const [k, v] of Object.entries(userCache.value)) {
          // eslint-disable-next-line no-unused-vars
          const { cachedAvatar, ...rest } = v
          cacheToSave[k] = rest
        }
        localStorage.setItem(STORAGE_KEY_USERS, JSON.stringify(cacheToSave))
      } catch (e) {
        console.error('保存聊天缓存失败:', e)
      }
    }, 500)
  }

  function _loadFromStorage() {
    try {
      const savedConvs = localStorage.getItem(STORAGE_KEY_CONVS)
      if (savedConvs) conversations.value = JSON.parse(savedConvs)
      const savedMsgs = localStorage.getItem(STORAGE_KEY_MSGS)
      if (savedMsgs) messages.value = JSON.parse(savedMsgs)
      const savedUsers = localStorage.getItem(STORAGE_KEY_USERS)
      if (savedUsers) userCache.value = JSON.parse(savedUsers)
    } catch (e) {
      console.error('加载聊天缓存失败:', e)
    }
    // 异步从 IndexedDB 恢复头像和图片 blob URL
    _restoreAllAvatarCache()
    _restoreAllImageCache()
  }

  // ========== 用户信息缓存 ==========
  const userCache = ref({}) // { [userId]: { name, avatar, avatarKey, cachedAvatar } }
  const _pendingAvatarKeys = new Set() // 正在恢复/下载中的 avatarKey，防止重复请求

  /**
   * 确保指定用户的头像已缓存（去重：同一 avatarKey 只处理一次）
   * 优先从 IndexedDB 恢复，未命中则从网络下载并存入 IndexedDB
   */
  function _ensureAvatarCached(userId, avatarKey, avatarUrl) {
    if (!avatarKey || !avatarUrl) return
    // 每个 avatarKey 在同一 session 内只尝试一次（防止 CORS 失败时反复重试）
    if (_pendingAvatarKeys.has(avatarKey)) return
    const current = userCache.value[String(userId)]
    if (current?.cachedAvatar?.startsWith('blob:') && current?.avatarKey === avatarKey) return

    _pendingAvatarKeys.add(avatarKey)
    restoreAvatar(avatarKey).then(blobUrl => {
      if (blobUrl) {
        // IndexedDB 有缓存，直接使用 blob URL（零网络请求）
        userCache.value[String(userId)] = { ...userCache.value[String(userId)], cachedAvatar: blobUrl }
      } else {
        // IndexedDB 无缓存，从网络下载并存入 IndexedDB（下次刷新即可从本地恢复）
        fetchAndCacheAvatar(avatarKey, avatarUrl).then(newBlobUrl => {
          if (userCache.value[String(userId)]?.avatarKey === avatarKey) {
            userCache.value[String(userId)] = {
              ...userCache.value[String(userId)],
              cachedAvatar: newBlobUrl || avatarUrl // CORS 失败时回退到原始 URL
            }
          }
        })
      }
    }).catch(() => {
      // IndexedDB 打开失败，回退到原始 URL
      if (userCache.value[String(userId)]?.avatarKey === avatarKey) {
        userCache.value[String(userId)] = {
          ...userCache.value[String(userId)],
          cachedAvatar: avatarUrl
        }
      }
    })
  }

  /**
   * 从 IndexedDB 恢复所有用户的头像 blob URL
   */
  function _restoreAllAvatarCache() {
    for (const [userId, info] of Object.entries(userCache.value)) {
      _ensureAvatarCached(userId, info.avatarKey, info.avatar)
    }
  }

  function _updateUserCache(userId, info) {
    if (!userId) return
    const key = String(userId)
    const existing = userCache.value[key]
    // 只在有实质内容时更新，避免空值覆盖已有数据
    const newName = info.name || existing?.name || ''
    const newAvatar = info.avatar || existing?.avatar || ''
    const newAvatarKey = info.avatarKey || existing?.avatarKey || ''
    if (newName || newAvatar) {
      // 通过 avatarKey 判断头像是否变更
      const avatarChanged = newAvatarKey && newAvatarKey !== existing?.avatarKey
      userCache.value[key] = {
        name: newName,
        avatar: newAvatar,
        avatarKey: newAvatarKey,
        cachedAvatar: avatarChanged ? '' : (existing?.cachedAvatar || '')
      }
      if (avatarChanged) {
        // avatarKey 变更：删除旧缓存，允许新 key 重试
        if (existing?.avatarKey) {
          removeAvatarCache(existing.avatarKey)
          _pendingAvatarKeys.delete(existing.avatarKey)
        }
        if (existing?.cachedAvatar?.startsWith('blob:')) URL.revokeObjectURL(existing.cachedAvatar)
        _ensureAvatarCached(key, newAvatarKey, newAvatar)
      } else {
        // avatarKey 未变，尝试缓存（session 内去重，不会重复请求）
        _ensureAvatarCached(key, newAvatarKey, newAvatar)
      }
    }
  }

  function _batchUpdateUserCache(members) {
    if (!Array.isArray(members)) return
    members.forEach(m => {
      _updateUserCache(m.id || m.userId, {
        name: m.name || m.nickname || '',
        avatar: m.avatar || m.avatarUrl || '',
        avatarKey: m.avatarKey || ''
      })
    })
  }

  function getUserInfo(userId) {
    if (!userId) return { name: '', avatar: '' }
    const info = userCache.value[String(userId)]
    if (!info) return { name: '', avatar: '' }
    // 有 avatarKey 时：只返回 cachedAvatar（blob URL 或异步恢复后的原始 URL），空则不渲染 <img>
    // 无 avatarKey 时：直接返回原始 URL（非 COS 图片，不走缓存）
    const avatar = info.avatarKey ? (info.cachedAvatar || '') : (info.avatar || '')
    return { name: info.name || '', avatar }
  }

  // ========== 聊天图片缓存 ==========
  const imageBlobCache = ref({}) // { [cacheKey]: blobUrl } — session 级，不持久化
  const _pendingImageKeys = new Set()

  /**
   * 确保指定图片已缓存（去重：同一 cacheKey 只处理一次）
   * 优先从 IndexedDB 恢复，未命中则从网络下载并存入 IndexedDB
   */
  function _ensureImageCached(imageUrl) {
    const cacheKey = getImageCacheKey(imageUrl)
    if (!cacheKey) return
    if (_pendingImageKeys.has(cacheKey)) return
    if (imageBlobCache.value[cacheKey]) return

    // 每个 cacheKey 在同一 session 内只尝试一次
    _pendingImageKeys.add(cacheKey)
    restoreImage(cacheKey).then(blobUrl => {
      if (blobUrl) {
        imageBlobCache.value[cacheKey] = blobUrl
      } else {
        fetchAndCacheImage(cacheKey, imageUrl).then(newBlobUrl => {
          if (newBlobUrl) {
            imageBlobCache.value[cacheKey] = newBlobUrl
          }
          // CORS 失败不设 fallback，getImageUrl 自动回退到原始 URL
        })
      }
    }).catch(() => {
      // IndexedDB 打开失败，getImageUrl 自动回退到原始 URL
    })
  }

  /**
   * 从 IndexedDB 恢复所有已缓存的聊天图片 blob URL
   */
  function _restoreAllImageCache() {
    for (const msgList of Object.values(messages.value)) {
      for (const msg of msgList) {
        if (msg.type === 'image' && msg.imageUrl) {
          _ensureImageCached(msg.imageUrl)
        }
      }
    }
  }

  /**
   * 获取图片的显示 URL（优先使用 IndexedDB 缓存的 blob URL）
   * @param {string} originalUrl 原始图片 URL
   * @returns {string} blob URL 或原始 URL
   */
  function getImageUrl(originalUrl) {
    if (!originalUrl) return ''
    const cacheKey = getImageCacheKey(originalUrl)
    if (!cacheKey) return originalUrl // blob: 或 data: URL 直接返回
    return imageBlobCache.value[cacheKey] || originalUrl
  }

  // ========== 状态 ==========
  const conversations = ref([])
  const currentConversationId = ref(null)
  const messages = ref({}) // { conversationId: [messages] }
  const loading = ref(false)
  const messageLoading = ref(false)
  const filterTab = ref('all') // all | internal | customer | group
  const searchKeyword = ref('')
  const showInfoPanel = ref(false)

  const replyToMessage = ref(null)
  const messageSearchKeyword = ref('')
  const messageSearchActive = ref(false)
  const showNewConversationDialog = ref(false)
  const availableContacts = ref([])

  // 游标分页
  const messageHasMore = ref({}) // { convId: boolean }

  // WebSocket 相关
  const wsConnected = ref(false)
  const typingUsers = ref({}) // { convId: { userId, name, timer } }
  const onlineUserIds = ref(new Set())

  // ========== 辅助函数 ==========
  function getCurrentUserId() {
    const userStore = useUserStore()
    return userStore.userInfo?.userId
  }

  // ========== 计算属性 ==========
  const currentConversation = computed(() => {
    return conversations.value.find(c => c.id === currentConversationId.value) || null
  })

  const currentMessages = computed(() => {
    const msgs = messages.value[currentConversationId.value] || []
    if (!messageSearchKeyword.value || !messageSearchActive.value) return msgs
    const keyword = messageSearchKeyword.value.toLowerCase()
    return msgs.filter(m =>
      m.type !== 'system' && m.content && m.content.toLowerCase().includes(keyword)
    )
  })

  const filteredConversations = computed(() => {
    let result = conversations.value

    // 按标签过滤
    if (filterTab.value !== 'all') {
      result = result.filter(c => c.type === filterTab.value)
    }

    // 按关键词搜索
    if (searchKeyword.value) {
      const keyword = searchKeyword.value.toLowerCase()
      result = result.filter(c =>
        c.name.toLowerCase().includes(keyword) ||
        (c.lastMessage && c.lastMessage.toLowerCase().includes(keyword))
      )
    }

    // 排序：置顶优先，然后按最后消息时间倒序
    return result.sort((a, b) => {
      const aPinned = a.pinned ? 1 : 0
      const bPinned = b.pinned ? 1 : 0
      if (aPinned !== bPinned) return bPinned - aPinned
      return new Date(b.lastMessageTime || 0) - new Date(a.lastMessageTime || 0)
    })
  })

  const totalUnreadCount = computed(() => {
    return conversations.value.reduce((sum, c) => sum + (c.unreadCount || 0), 0)
  })

  const messageSearchResults = computed(() => {
    if (!messageSearchKeyword.value || !messageSearchActive.value) return []
    const msgs = messages.value[currentConversationId.value] || []
    const keyword = messageSearchKeyword.value.toLowerCase()
    return msgs.filter(m =>
      m.type !== 'system' && m.content && m.content.toLowerCase().includes(keyword)
    )
  })

  const currentUserRole = computed(() => {
    const conv = currentConversation.value
    if (!conv || conv.type !== 'group') return null
    const currentUserId = getCurrentUserId()
    const me = conv.members?.find(m => String(m.id) === String(currentUserId))
    return me?.role || 'member'
  })

  // ========== 会话管理 ==========

  let _conversationsLoaded = false

  function loadConversations() {
    if (_conversationsLoaded && conversations.value.length > 0) return
    loading.value = true
    _loadFromStorage()
    _conversationsLoaded = true
    loading.value = false
  }

  function selectConversation(id) {
    currentConversationId.value = id
    replyToMessage.value = null
    messageSearchKeyword.value = ''
    messageSearchActive.value = false

    // 标记已读
    const conv = conversations.value.find(c => c.id === id)
    if (conv && conv.unreadCount > 0) {
      conv.unreadCount = 0
      chatApi.markConversationRead(id).catch(console.error)
      _saveToStorage()
    }

    // 每次进入会话都拉取最新成员信息
    if (conv) {
      _fetchAndApplyMembers(id)
    }
  }

  /**
   * 拉取会话成员列表并更新本地数据
   * 群聊：更新 members 和 memberCount
   * 单聊：额外将对方的 department/position/phone/email 写入会话对象
   */
  function _fetchAndApplyMembers(convId) {
    chatApi.getConversationMembers(convId).then(memberList => {
      if (!Array.isArray(memberList)) return
      const conv = conversations.value.find(c => c.id === convId)
      if (!conv) return

      conv.members = memberList.map(m => ({
        id: m.userId || m.id,
        name: m.nickname || m.username || m.name || '',
        avatar: m.avatar || m.avatarUrl || '',
        avatarKey: m.avatarKey || '',
        role: m.role === 2 ? 'owner' : m.role === 1 ? 'admin' : 'member',
        department: m.department || m.deptName || '',
        position: m.position || m.postNames || '',
        phone: m.phone || '',
        email: m.email || '',
        source: m.source || '',
        customerLevel: m.customerLevel || ''
      }))
      conv.memberCount = conv.members.length

      // 更新用户信息缓存
      _batchUpdateUserCache(conv.members)

      // 单聊：将对方信息写入会话对象
      if (conv.type !== 'group') {
        const currentUserId = getCurrentUserId()
        const other = conv.members.find(m => String(m.id) !== String(currentUserId))
        if (other) {
          if (!conv.name) conv.name = other.name
          if (!conv.avatar) conv.avatar = other.avatar
          conv.department = other.department || conv.department || ''
          conv.position = other.position || conv.position || ''
          conv.phone = other.phone || conv.phone || ''
          conv.email = other.email || conv.email || ''
          conv.source = other.source || conv.source || ''
          conv.customerLevel = other.customerLevel || conv.customerLevel || ''
        }
      }

      _saveToStorage()
    }).catch(e => console.error('拉取会话成员失败:', e))
  }

  // ========== 消息管理 ==========

  function loadMoreMessages() {
    return Promise.resolve(false)
  }

  async function sendNewMessage(content, type = 'text', extra = {}) {
    if (!currentConversationId.value) return
    if (type === 'text' && !content.trim()) return

    const currentUserId = getCurrentUserId()
    const convId = currentConversationId.value
    const tempId = 'temp-' + Date.now() + '-' + Math.random().toString(36).substring(2, 8)

    // 构建乐观消息
    const optimisticMsg = {
      id: tempId,
      senderId: currentUserId,
      senderName: '我',
      content: type === 'text' ? content.trim() : (extra.fileName || ''),
      time: new Date().toISOString(),
      type,
      status: 'sending',
      _tempId: tempId
    }

    // 文件/图片扩展字段
    if (type === 'file') {
      optimisticMsg.fileName = extra.fileName || ''
      optimisticMsg.fileSize = extra.fileSize || ''
      optimisticMsg.fileUrl = extra.fileUrl || ''
    }
    if (type === 'image') {
      optimisticMsg.imageUrl = extra.imageUrl || ''
    }

    // 引用回复
    const replyMsgId = replyToMessage.value?.id || null
    if (replyToMessage.value) {
      optimisticMsg.replyTo = {
        id: replyToMessage.value.id,
        senderName: replyToMessage.value.senderName,
        content: replyToMessage.value.content || replyToMessage.value.fileName || '[文件]'
      }
      replyToMessage.value = null
    }

    // 乐观追加到消息列表
    if (!messages.value[convId]) {
      messages.value[convId] = []
    }
    messages.value[convId].push(optimisticMsg)

    // 更新会话最后消息
    _updateConversationLastMessage(convId, optimisticMsg)
    _saveToStorage()

    // 通过 REST API 发送
    try {
      const reqData = {
        conversationId: convId,
        msgType: MSG_TYPE_REVERSE[type] || 1,
        content: type === 'text' ? content.trim() : ''
      }
      if (replyMsgId) {
        reqData.replyMsgId = replyMsgId
      }

      const data = await chatApi.sendMessage(reqData)

      // 用服务端返回的真实消息替换临时消息
      const convMsgs = messages.value[convId]
      if (convMsgs) {
        const idx = convMsgs.findIndex(m => m._tempId === tempId)
        if (idx !== -1) {
          const optimistic = convMsgs[idx]
          const realMsg = transformMessage(data, currentUserId)
          realMsg.status = 'delivered'
          if (!realMsg.time) realMsg.time = optimistic.time
          convMsgs.splice(idx, 1, realMsg)
        }
      }
      _saveToStorage()
    } catch (error) {
      // 标记发送失败
      const convMsgs = messages.value[convId]
      if (convMsgs) {
        const msg = convMsgs.find(m => m._tempId === tempId)
        if (msg) msg.status = 'failed'
      }
      _saveToStorage()
      console.error('发送消息失败:', error)
    }
  }

  async function sendFileMessage(file) {
    if (!currentConversationId.value) return

    const convId = currentConversationId.value
    const currentUserId = getCurrentUserId()
    const tempId = 'temp-' + Date.now() + '-' + Math.random().toString(36).substring(2, 8)

    // 构建乐观消息
    const optimisticMsg = {
      id: tempId,
      senderId: currentUserId,
      senderName: '我',
      content: file.name || '[文件]',
      time: new Date().toISOString(),
      type: 'file',
      fileName: file.name || '',
      fileSize: file.size ? (file.size / 1024).toFixed(1) + ' KB' : '',
      fileUrl: '',
      status: 'sending',
      _tempId: tempId
    }

    if (!messages.value[convId]) {
      messages.value[convId] = []
    }
    messages.value[convId].push(optimisticMsg)
    _updateConversationLastMessage(convId, optimisticMsg)
    _saveToStorage()

    try {
      const data = await chatApi.uploadFile(convId, file)
      // 用服务端返回的真实消息替换临时消息
      const convMsgs = messages.value[convId]
      if (convMsgs) {
        const idx = convMsgs.findIndex(m => m._tempId === tempId)
        if (idx !== -1) {
          if (data && (data.id || data.messageId)) {
            const realMsg = transformMessage(data, currentUserId)
            realMsg.status = 'delivered'
            // 确保自己发的消息始终有正确的 senderId 和 senderName
            if (!realMsg.senderId) realMsg.senderId = currentUserId
            if (!realMsg.senderName) realMsg.senderName = '我'
            if (!realMsg.time) realMsg.time = convMsgs[idx].time
            // 确保文件字段不丢失
            if (!realMsg.fileName) realMsg.fileName = convMsgs[idx].fileName
            if (!realMsg.fileSize) realMsg.fileSize = convMsgs[idx].fileSize
            convMsgs.splice(idx, 1, realMsg)
          } else {
            convMsgs[idx].status = 'delivered'
          }
        }
      }
      _saveToStorage()
    } catch (error) {
      // 标记发送失败
      const convMsgs = messages.value[convId]
      if (convMsgs) {
        const msg = convMsgs.find(m => m._tempId === tempId)
        if (msg) msg.status = 'failed'
      }
      _saveToStorage()
      ElMessage.error('文件上传失败')
      console.error('文件上传失败:', error)
    }
  }

  async function sendImageMessage(file) {
    if (!currentConversationId.value) return

    const convId = currentConversationId.value
    const currentUserId = getCurrentUserId()
    const tempId = 'temp-' + Date.now() + '-' + Math.random().toString(36).substring(2, 8)
    const blobUrl = URL.createObjectURL(file)

    // 构建乐观消息（本地预览）
    const optimisticMsg = {
      id: tempId,
      senderId: currentUserId,
      senderName: '我',
      content: '[图片]',
      time: new Date().toISOString(),
      type: 'image',
      imageUrl: blobUrl,
      status: 'sending',
      _tempId: tempId
    }

    if (!messages.value[convId]) {
      messages.value[convId] = []
    }
    messages.value[convId].push(optimisticMsg)
    _updateConversationLastMessage(convId, optimisticMsg)
    _saveToStorage()

    try {
      const data = await chatApi.uploadFile(convId, file)
      // 用服务端返回的真实消息替换临时消息
      const convMsgs = messages.value[convId]
      if (convMsgs) {
        const idx = convMsgs.findIndex(m => m._tempId === tempId)
        if (idx !== -1) {
          if (data && (data.id || data.messageId)) {
            const realMsg = transformMessage(data, currentUserId)
            realMsg.status = 'delivered'
            // 确保自己发的消息始终有正确的 senderId 和 senderName
            if (!realMsg.senderId) realMsg.senderId = currentUserId
            if (!realMsg.senderName) realMsg.senderName = '我'
            if (!realMsg.type || realMsg.type === 'text') realMsg.type = 'image'
            if (!realMsg.time) realMsg.time = convMsgs[idx].time
            // 若 transformMessage 未能解析出 imageUrl，尝试从响应中直接提取
            if (!realMsg.imageUrl) {
              try {
                const extra = typeof data.extra === 'string' ? JSON.parse(data.extra) : data.extra
                realMsg.imageUrl = extra?.fileUrl || data.fileUrl || ''
              } catch (_) { /* ignore */ }
            }
            // 仍然没有则保留 blob URL（不 revoke）
            if (!realMsg.imageUrl) {
              realMsg.imageUrl = blobUrl
              // 不 revoke，让 blob 继续显示直到 WS new_msg 替换
            } else {
              URL.revokeObjectURL(blobUrl)
              // 缓存服务端返回的图片 URL
              _ensureImageCached(realMsg.imageUrl)
            }
            convMsgs.splice(idx, 1, realMsg)
          } else {
            // API 未返回消息 DTO，保留 blob 预览，仅更新状态
            convMsgs[idx].status = 'delivered'
            // 不 revoke blob，让图片继续显示
          }
        } else {
          // 已被 WS new_msg 替换，可以安全 revoke
          URL.revokeObjectURL(blobUrl)
        }
      } else {
        URL.revokeObjectURL(blobUrl)
      }
      _saveToStorage()
    } catch (error) {
      // 标记发送失败，保留 blob 预览
      const convMsgs = messages.value[convId]
      if (convMsgs) {
        const msg = convMsgs.find(m => m._tempId === tempId)
        if (msg) msg.status = 'failed'
      }
      _saveToStorage()
      ElMessage.error('图片上传失败')
      console.error('图片上传失败:', error)
    }
  }

  async function deleteMessage(messageId) {
    // 对自己发的消息执行撤回
    try {
      await chatApi.revokeMessage(messageId)
      // 本地更新为撤回状态
      const convMsgs = messages.value[currentConversationId.value]
      if (convMsgs) {
        const msg = convMsgs.find(m => m.id === messageId)
        if (msg) {
          msg.type = 'system'
          msg.content = `${msg.senderName} 撤回了一条消息`
          _saveToStorage()
        }
      }
    } catch (error) {
      ElMessage.error(error.message || '撤回失败')
    }
  }

  // ========== 回复与搜索 ==========

  function setReplyTo(message) {
    replyToMessage.value = message
  }

  function clearReplyTo() {
    replyToMessage.value = null
  }

  function toggleInfoPanel() {
    showInfoPanel.value = !showInfoPanel.value
  }

  function toggleMessageSearch() {
    messageSearchActive.value = !messageSearchActive.value
    if (!messageSearchActive.value) {
      messageSearchKeyword.value = ''
    }
  }

  // ========== 会话设置 ==========

  async function pinConversation(convId) {
    const conv = conversations.value.find(c => c.id === convId)
    if (!conv) return
    const newPinned = !conv.pinned
    try {
      await chatApi.updateConversationSettings(convId, { pinned: newPinned })
      conv.pinned = newPinned
      _saveToStorage()
    } catch (error) {
      ElMessage.error('操作失败')
    }
  }

  async function muteConversation(convId) {
    const conv = conversations.value.find(c => c.id === convId)
    if (!conv) return
    const newMuted = !conv.muted
    try {
      await chatApi.updateConversationSettings(convId, { muted: newMuted })
      conv.muted = newMuted
      _saveToStorage()
    } catch (error) {
      ElMessage.error('操作失败')
    }
  }

  function deleteConversation(convId) {
    const index = conversations.value.findIndex(c => c.id === convId)
    if (index > -1) {
      conversations.value.splice(index, 1)
    }
    if (currentConversationId.value === convId) {
      currentConversationId.value = null
    }
    delete messages.value[convId]
    _saveToStorage()
  }

  function isConversationPinned(convId) {
    const conv = conversations.value.find(c => c.id === convId)
    return conv?.pinned || false
  }

  function isConversationMuted(convId) {
    const conv = conversations.value.find(c => c.id === convId)
    return conv?.muted || false
  }

  // ========== 联系人与新建会话 ==========

  async function loadContacts(params = {}) {
    try {
      const currentUserId = getCurrentUserId()
      // 并行加载内部人员和APP客户
      const [internalRes, customerRes] = await Promise.all([
        getUserList({ status: 0, page: 1, size: 999, ...params }),
        getAppUserList({ status: 0, page: 1, size: 999 })
      ])
      // 内部人员
      const internalRecords = (internalRes?.records || [])
        .filter(u => u.id !== currentUserId)
        .map(u => ({
          id: u.id,
          name: u.displayName || u.nickname || u.username || '',
          type: 'internal',
          department: u.deptName || '',
          position: u.postNames || '',
          phone: u.phone || '',
          email: u.email || '',
          avatar: u.avatar || '',
          avatarKey: u.avatarKey || ''
        }))
      // APP客户
      const customerRecords = (customerRes?.records || []).map(u => ({
        id: `app_${u.id}`,
        appUserId: u.id,
        name: u.nickname || u.phone || '',
        type: 'customer',
        phone: u.phone || '',
        avatar: u.avatar || '',
        gender: u.gender
      }))
      availableContacts.value = [...internalRecords, ...customerRecords]
    } catch (error) {
      console.error('加载联系人失败:', error)
    }
  }

  async function createNewConversation(contact) {
    try {
      // 根据contact类型获取userType和真正的userId
      let targetUserId = contact.id
      let targetUserType = null

      if (contact.type === 'customer') {
        // 客户: id是app_xxx格式，需要提取真正的userId，userType=2
        targetUserId = contact.appUserId
        targetUserType = 2
      } else if (contact.type === 'internal') {
        // 内部用户: userType=1
        targetUserType = 1
      }

      const data = await chatApi.createSingleConversation(targetUserId, targetUserType)
      const newConv = transformConversation(data)

      // 缓存联系人信息
      _updateUserCache(contact.id, { name: contact.name, avatar: contact.avatar, avatarKey: contact.avatarKey || '' })
      // 缓存当前用户信息
      const userStore = useUserStore()
      _updateUserCache(getCurrentUserId(), { name: userStore.userInfo?.displayName || userStore.userInfo?.username, avatar: userStore.userInfo?.avatar, avatarKey: userStore.userInfo?.avatarKey || '' })

      // 单聊时补充对方信息
      if (!newConv.name) newConv.name = contact.name || ''
      if (!newConv.avatar) newConv.avatar = contact.avatar || ''
      newConv.department = contact.department || ''
      newConv.position = contact.position || ''
      newConv.phone = contact.phone || ''
      newConv.email = contact.email || ''

      // 存储成员信息（如果后端未返回）
      if (newConv.members.length === 0) {
        const currentUserId = getCurrentUserId()
        const userStore = useUserStore()
        newConv.members = [
          {
            id: currentUserId,
            name: userStore.userInfo?.displayName || userStore.userInfo?.username || '我',
            avatar: userStore.userInfo?.avatar || '',
            avatarKey: userStore.userInfo?.avatarKey || '',
            role: 'member'
          },
          {
            id: contact.id,
            name: contact.name || '',
            avatar: contact.avatar || '',
            avatarKey: contact.avatarKey || '',
            role: 'member',
            department: contact.department || '',
            position: contact.position || '',
            phone: contact.phone || '',
            email: contact.email || ''
          }
        ]
        newConv.memberCount = 2
      }

      // 检查是否已存在
      const existing = conversations.value.find(c => c.id === newConv.id)
      if (existing) {
        // 更新已有会话的成员和对方信息
        if (!existing.name) existing.name = newConv.name
        if (!existing.avatar) existing.avatar = newConv.avatar
        if (existing.members.length === 0) existing.members = newConv.members
        existing.department = newConv.department
        existing.position = newConv.position
        existing.phone = newConv.phone
        existing.email = newConv.email
        selectConversation(existing.id)
      } else {
        conversations.value.unshift(newConv)
        selectConversation(newConv.id)
      }
      showNewConversationDialog.value = false
      _saveToStorage()
    } catch (error) {
      ElMessage.error('创建会话失败')
      console.error(error)
    }
  }

  async function createNewGroup(groupData) {
    try {
      const data = await chatApi.createGroupConversation({
        type: 2,
        name: groupData.name,
        memberUserIds: groupData.members.map(m => m.id)
      })
      const newConv = transformConversation(data)

      // 缓存群成员信息
      _batchUpdateUserCache(groupData.members)
      const userStore = useUserStore()
      _updateUserCache(getCurrentUserId(), { name: userStore.userInfo?.displayName || userStore.userInfo?.username, avatar: userStore.userInfo?.avatar, avatarKey: userStore.userInfo?.avatarKey || '' })

      // 存储成员信息（如果后端未返回）
      if (newConv.members.length === 0) {
        const currentUserId = getCurrentUserId()
        const userStore = useUserStore()
        newConv.members = [
          {
            id: currentUserId,
            name: userStore.userInfo?.displayName || userStore.userInfo?.username || '我',
            avatar: userStore.userInfo?.avatar || '',
            avatarKey: userStore.userInfo?.avatarKey || '',
            role: 'owner'
          },
          ...groupData.members.map(m => ({
            id: m.id,
            name: m.name || '',
            avatar: m.avatar || '',
            avatarKey: m.avatarKey || '',
            role: 'member',
            department: m.department || '',
            position: m.position || '',
            phone: m.phone || '',
            email: m.email || ''
          }))
        ]
        newConv.memberCount = newConv.members.length
        newConv.ownerId = currentUserId
      }

      conversations.value.unshift(newConv)
      selectConversation(newConv.id)
      showNewConversationDialog.value = false
      _saveToStorage()
    } catch (error) {
      ElMessage.error('创建群聊失败')
      console.error(error)
    }
  }

  // ========== 群聊管理 ==========

  async function addGroupMembers(convId, newMembers) {
    try {
      // 构建成员列表，区分平台端用户和APP端用户
      const members = newMembers.map(m => {
        if (m.type === 'customer') {
          // APP用户: id格式为 app_xxx，需要提取真正的userId，userType=2
          return { userId: m.appUserId, userType: 2 }
        } else {
          // 平台端用户: userType=1
          return { userId: m.id, userType: 1 }
        }
      })
      await chatApi.addGroupMembers(convId, { members })
      // 本地添加成员
      const conv = conversations.value.find(c => c.id === convId)
      if (conv) {
        for (const m of newMembers) {
          if (!conv.members.some(em => em.id === m.id)) {
            conv.members.push({
              id: m.id,
              name: m.name,
              avatar: m.avatar || '',
              role: 'member',
              userType: m.type === 'customer' ? 2 : 1
            })
          }
        }
        conv.memberCount = conv.members.length
        _saveToStorage()
      }
    } catch (error) {
      ElMessage.error('添加成员失败')
      console.error(error)
    }
  }

  async function removeGroupMember(convId, memberId) {
    try {
      // 获取成员信息以获取 userType
      const conv = conversations.value.find(c => c.id === convId)
      const member = conv?.members.find(m => m.id === memberId)
      const userType = member?.userType || 1

      // 如果是APP用户，需要提取真正的userId
      let realUserId = memberId
      if (String(memberId).startsWith('app_')) {
        realUserId = memberId.replace('app_', '')
      }

      await chatApi.removeGroupMember(convId, { userId: Number(realUserId), userType })

      if (conv) {
        conv.members = conv.members.filter(m => m.id !== memberId)
        conv.memberCount = conv.members.length
        _saveToStorage()
      }
    } catch (error) {
      ElMessage.error('移除成员失败')
      console.error(error)
    }
  }

  async function setMemberRole(convId, memberId, role) {
    try {
      await chatApi.setMemberRole(convId, {
        userId: memberId,
        role: ROLE_REVERSE[role] ?? 0
      })
      const conv = conversations.value.find(c => c.id === convId)
      if (conv) {
        const member = conv.members.find(m => m.id === memberId)
        if (member) member.role = role
        _saveToStorage()
      }
    } catch (error) {
      ElMessage.error('设置角色失败')
      console.error(error)
    }
  }

  async function dissolveGroup(convId) {
    try {
      await chatApi.dissolveGroup(convId)
      deleteConversation(convId)
    } catch (error) {
      ElMessage.error('解散群聊失败')
      console.error(error)
    }
  }

  async function leaveGroup(convId) {
    try {
      await chatApi.leaveGroup(convId)
      deleteConversation(convId)
    } catch (error) {
      ElMessage.error('退出群聊失败')
      console.error(error)
    }
  }

  async function transferOwner(convId, memberId) {
    try {
      await chatApi.transferOwner(convId, { newOwnerId: memberId })
      const conv = conversations.value.find(c => c.id === convId)
      if (conv) {
        // 原群主降为管理员
        const oldOwner = conv.members.find(m => m.role === 'owner')
        if (oldOwner) oldOwner.role = 'admin'
        // 新群主
        const newOwner = conv.members.find(m => m.id === memberId)
        if (newOwner) newOwner.role = 'owner'
        conv.ownerId = memberId
        _saveToStorage()
      }
    } catch (error) {
      ElMessage.error('转让群主失败')
      console.error(error)
    }
  }

  async function updateGroupInfo(convId, data) {
    try {
      const updateData = {}
      if (data.name !== undefined) updateData.name = data.name
      if (data.description !== undefined) updateData.notice = data.description
      await chatApi.updateGroupInfo(convId, updateData)
      // 本地更新
      const conv = conversations.value.find(c => c.id === convId)
      if (conv) {
        if (data.name !== undefined) conv.name = data.name
        if (data.description !== undefined) conv.description = data.description
        _saveToStorage()
      }
    } catch (error) {
      ElMessage.error('更新群信息失败')
      console.error(error)
    }
  }

  // ========== WebSocket 管理 ==========

  let _wsInitialized = false

  function initWebSocket() {
    if (_wsInitialized) return
    _wsInitialized = true

    const ws = getIMWebSocket()

    // 连接成功 + 认证成功
    ws.on('connected', () => {
      wsConnected.value = true
    })

    ws.on('disconnected', () => {
      wsConnected.value = false
    })

    ws.on('auth_failed', () => {
      ElMessage.error('IM 认证失败，请重新登录')
    })

    // 新消息推送
    ws.on('new_msg', (data) => {
      const currentUserId = getCurrentUserId()
      const msg = transformMessage(data, currentUserId)
      const convId = data.conversationId

      // 补充缺失的发送者名称：优先从缓存获取，其次从会话成员列表查找
      if (msg.senderId && String(msg.senderId) !== String(currentUserId)) {
        const cached = getUserInfo(msg.senderId)
        if (!msg.senderName && cached.name) {
          msg.senderName = cached.name
        }
        if (!msg.senderName) {
          const conv = conversations.value.find(c => c.id === convId)
          if (conv) {
            const member = conv.members?.find(m => String(m.id) === String(msg.senderId))
            msg.senderName = member?.name || conv.name || ''
          }
        }
        // 更新用户缓存（WS 消息可能携带发送者信息）
        _updateUserCache(msg.senderId, {
          name: data.senderName || data.senderNickname || data.nickname || data.fromNickname || msg.senderName || '',
          avatar: data.senderAvatar || data.avatar || '',
          avatarKey: data.senderAvatarKey || data.avatarKey || ''
        })
      }

      // 确保消息列表存在
      if (!messages.value[convId]) {
        messages.value[convId] = []
      }

      const convMsgs = messages.value[convId]
      let isNewMsg = false

      if (String(msg.senderId) === String(currentUserId)) {
        // 查找待替换的临时消息（sending 或已被 REST 标记为 delivered 但仍有 _tempId）
        const tempIdx = convMsgs.findIndex(m => m._tempId && m.type === msg.type)
        if (tempIdx !== -1) {
          // 释放可能存在的 blob URL
          const oldImageUrl = convMsgs[tempIdx].imageUrl
          if (oldImageUrl && oldImageUrl.startsWith('blob:')) {
            URL.revokeObjectURL(oldImageUrl)
          }
          msg.status = 'delivered'
          convMsgs.splice(tempIdx, 1, msg)
        } else if (!convMsgs.some(m => m.id === msg.id)) {
          // REST 已处理过，仅在不重复时追加
          msg.status = 'delivered'
          convMsgs.push(msg)
        }
      } else {
        // 他人消息：避免重复后追加
        if (!convMsgs.some(m => m.id === msg.id)) {
          convMsgs.push(msg)
          isNewMsg = true
        }
      }

      // 缓存图片消息
      if (msg.type === 'image' && msg.imageUrl) {
        _ensureImageCached(msg.imageUrl)
      }

      // 更新会话列表
      const isSelfMsg = String(msg.senderId) === String(currentUserId)
      let conv = conversations.value.find(c => c.id === convId)
      if (conv) {
        conv.lastMessage = formatLastMessage(msg)
        conv.lastMessageTime = msg.time
        if (!isSelfMsg && isNewMsg && convId !== currentConversationId.value) {
          conv.unreadCount = (conv.unreadCount || 0) + 1
        } else if (!isSelfMsg && convId === currentConversationId.value) {
          chatApi.markConversationRead(convId).catch(console.error)
        }
      } else {
        // 新会话不在本地列表中，先用临时信息创建本地会话
        const newConv = {
          id: convId,
          type: data.conversationType === 2 ? 'group' : 'internal',
          name: data.senderName || '',
          avatar: '',
          lastMessage: formatLastMessage(msg),
          lastMessageTime: msg.time,
          unreadCount: isSelfMsg ? 0 : 1,
          online: false,
          department: '', position: '', phone: '', email: '',
          source: '', customerLevel: '',
          memberCount: 0, description: '', ownerId: null,
          members: [], pinned: false, muted: false,
          _raw: { type: data.conversationType || 1, sourceType: data.sourceType || 1 }
        }
        conversations.value.unshift(newConv)
        // 异步拉取会话详情，获取正确的群名、成员等信息
        chatApi.getConversationDetail(convId).then(apiConv => {
          const detail = transformConversation(apiConv)
          const conv = conversations.value.find(c => c.id === convId)
          if (conv) {
            conv.name = detail.name || conv.name
            conv.type = detail.type
            conv.avatar = detail.avatar || conv.avatar
            conv.memberCount = detail.memberCount || conv.memberCount
            conv.ownerId = detail.ownerId || conv.ownerId
            conv.description = detail.description || conv.description
            conv.pinned = detail.pinned
            conv.muted = detail.muted
            if (detail.members.length > 0) conv.members = detail.members
            conv._raw = detail._raw
            _saveToStorage()
            // ConversationDTO 不含成员详情，额外拉取成员列表
            if (!conv.members || conv.members.length === 0) {
              _fetchAndApplyMembers(convId)
            }
          }
        }).catch(e => console.error('拉取会话详情失败:', e))
      }

      // 非 chat 页面收到他人消息时，右下角弹出通知
      if (!isSelfMsg && router.currentRoute.value.path !== '/chat') {
        const cachedSender = getUserInfo(msg.senderId)
        const senderName = cachedSender.name || msg.senderName || '未知用户'
        const senderAvatar = cachedSender.avatar || ''
        const contentPreview = msg.type === 'image' ? '[图片]'
          : msg.type === 'file' ? '[文件]'
          : (msg.content || '').slice(0, 50) || '[消息]'

        const avatarNode = senderAvatar
          ? h('img', { src: senderAvatar, style: 'width:36px;height:36px;border-radius:50%;object-fit:cover;flex-shrink:0' })
          : h('div', {
              style: 'width:36px;height:36px;border-radius:50%;background:#6366f1;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0'
            }, senderName.slice(-1))

        ElNotification({
          title: '',
          duration: 1000,
          position: 'bottom-right',
          onClick: () => {
            router.push('/chat')
          },
          message: h('div', {
            style: 'display:flex;align-items:center;gap:10px;cursor:pointer'
          }, [
            avatarNode,
            h('div', { style: 'min-width:0' }, [
              h('div', { style: 'font-size:14px;font-weight:600;color:#1e293b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis' }, senderName),
              h('div', { style: 'font-size:12px;color:#94a3b8;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:200px' }, contentPreview)
            ])
          ])
        })
      }

      _saveToStorage()
    })

    // 已读回执
    ws.on('read_receipt', (data) => {
      const { conversationId, messageId, userId, readTime } = data
      const convMsgs = messages.value[conversationId]
      if (!convMsgs) return

      const currentUserId = getCurrentUserId()
      const conv = conversations.value.find(c => c.id === conversationId)
      const isGroup = conv?.type === 'group'
      const targetId = Number(messageId)
      let changed = false

      if (isGroup) {
        // 群聊：按用户追踪已读
        const userName = _getMemberName(conversationId, userId)
        convMsgs.forEach(msg => {
          if (String(msg.senderId) === String(currentUserId) && !String(msg.id).startsWith('temp-') && Number(msg.id) <= targetId) {
            if (!msg.readBy) msg.readBy = []
            if (!msg.readBy.some(r => String(r.userId) === String(userId))) {
              msg.readBy.push({ userId, userName: userName || `用户${userId}`, readTime: readTime || new Date().toISOString() })
              changed = true
            }
          }
        })
      } else {
        // 单聊：保持二态已读逻辑
        convMsgs.forEach(msg => {
          if (String(msg.senderId) === String(currentUserId) && msg.status !== 'read' && !String(msg.id).startsWith('temp-') && Number(msg.id) <= targetId) {
            msg.status = 'read'
            changed = true
          }
        })
      }
      if (changed) _saveToStorage()
    })

    // 输入状态
    ws.on('typing_notify', (data) => {
      const { conversationId, userId, nickname } = data
      // 清除之前的定时器
      if (typingUsers.value[conversationId]?.timer) {
        clearTimeout(typingUsers.value[conversationId].timer)
      }
      const timer = setTimeout(() => {
        delete typingUsers.value[conversationId]
        typingUsers.value = { ...typingUsers.value }
      }, 3000)
      typingUsers.value[conversationId] = { userId, name: nickname, timer }
      typingUsers.value = { ...typingUsers.value } // 触发响应式
    })

    // 用户上线
    ws.on('user_online', (data) => {
      const { userId } = data
      onlineUserIds.value.add(userId)
      onlineUserIds.value = new Set(onlineUserIds.value)
      // 更新对应单聊会话的在线状态
      conversations.value.forEach(c => {
        if (c.type !== 'group') {
          const other = c.members?.find(m => m.id === userId)
          if (other) {
            c.online = true
            other.online = true
          }
        }
      })
    })

    // 用户下线
    ws.on('user_offline', (data) => {
      const { userId } = data
      onlineUserIds.value.delete(userId)
      onlineUserIds.value = new Set(onlineUserIds.value)
      conversations.value.forEach(c => {
        if (c.type !== 'group') {
          const other = c.members?.find(m => m.id === userId)
          if (other) {
            c.online = false
            other.online = false
          }
        }
      })
    })

    // 系统通知（群成员变更、角色变更等）
    ws.on('system_notify', (data) => {
      const { conversationId, action, message } = data

      // 追加系统消息气泡
      if (conversationId && message) {
        if (!messages.value[conversationId]) {
          messages.value[conversationId] = []
        }
        messages.value[conversationId].push({
          id: 'sys-' + Date.now() + '-' + Math.random().toString(36).substring(2, 6),
          senderId: 0,
          senderName: '',
          content: message,
          time: new Date().toISOString(),
          type: 'system'
        })
      }

      const conv = conversations.value.find(c => c.id === conversationId)

      switch (action) {
        case 'member_added': {
          // 有新人加入，刷新成员列表
          if (conv) {
            _fetchAndApplyMembers(conversationId)
          }
          break
        }

        case 'member_invited': {
          // 自己被邀请加入群聊，拉取会话详情加入列表
          if (!conv) {
            chatApi.getConversationDetail(conversationId).then(apiConv => {
              const detail = transformConversation(apiConv)
              // 再次检查避免重复
              if (!conversations.value.find(c => c.id === conversationId)) {
                conversations.value.unshift(detail)
                // 额外拉取成员列表
                _fetchAndApplyMembers(conversationId)
              }
              _saveToStorage()
            }).catch(e => console.error('拉取会话详情失败:', e))
          } else {
            // 会话已存在（可能由 new_msg 先创建），刷新详情
            chatApi.getConversationDetail(conversationId).then(apiConv => {
              const detail = transformConversation(apiConv)
              conv.name = detail.name || conv.name
              conv.type = detail.type
              conv.avatar = detail.avatar || conv.avatar
              conv.memberCount = detail.memberCount || conv.memberCount
              conv.ownerId = detail.ownerId || conv.ownerId
              conv.description = detail.description || conv.description
              conv._raw = detail._raw
              _saveToStorage()
              // 额外拉取成员列表
              _fetchAndApplyMembers(conversationId)
            }).catch(e => console.error('刷新会话详情失败:', e))
          }
          break
        }

        case 'member_removed_self': {
          // 自己被移出群聊，从会话列表移除
          if (conv) {
            ElMessage.warning(message || '你已被移出群聊')
            const index = conversations.value.findIndex(c => c.id === conversationId)
            if (index > -1) conversations.value.splice(index, 1)
            if (currentConversationId.value === conversationId) {
              currentConversationId.value = null
            }
            delete messages.value[conversationId]
          }
          break
        }

        case 'member_removed': {
          // 有人被移出，从本地成员列表移除
          if (conv && data.removedUserId) {
            conv.members = conv.members.filter(m => String(m.id) !== String(data.removedUserId))
            conv.memberCount = conv.members.length
          }
          break
        }

        case 'member_quit': {
          // 有人退出，从本地成员列表移除
          if (conv && data.userId) {
            conv.members = conv.members.filter(m => String(m.id) !== String(data.userId))
            conv.memberCount = conv.members.length
          }
          break
        }

        case 'role_changed': {
          // 角色变更，更新本地成员角色
          if (conv && data.userId) {
            const member = conv.members.find(m => String(m.id) === String(data.userId))
            if (member) {
              member.role = data.newRole === 2 ? 'owner' : data.newRole === 1 ? 'admin' : 'member'
            }
          }
          break
        }

        case 'ownership_transferred': {
          // 群主变更
          if (conv) {
            // 原群主降为管理员
            const oldOwner = conv.members.find(m => m.role === 'owner')
            if (oldOwner) oldOwner.role = 'admin'
            // 新群主
            if (data.newOwnerId) {
              const newOwner = conv.members.find(m => String(m.id) === String(data.newOwnerId))
              if (newOwner) newOwner.role = 'owner'
              conv.ownerId = data.newOwnerId
            }
          }
          break
        }

        case 'customer_consult': {
          // 客户咨询，创建新会话
          if (!conv) {
            chatApi.getConversationDetail(conversationId).then(apiConv => {
              const detail = transformConversation(apiConv)
              // 再次检查避免重复
              if (!conversations.value.find(c => c.id === conversationId)) {
                conversations.value.unshift(detail)
                // 额外拉取成员列表
                _fetchAndApplyMembers(conversationId)

                // 更新最后消息和未读数（如果不在当前会话）
                if (conversationId !== currentConversationId.value) {
                  detail.lastMessage = message || '微信用户 发起咨询'
                  detail.lastMessageTime = new Date(data.timestamp).toISOString()
                  detail.unreadCount = 1
                }
              }
              _saveToStorage()

              // 弹窗提示
              if (conversationId !== currentConversationId.value) {
                const senderName = data.appUserName || '微信用户'
                const contentPreview = message || '发起咨询'

                const avatarNode = detail.avatar
                  ? h('img', { src: detail.avatar, style: 'width:36px;height:36px;border-radius:50%;object-fit:cover;flex-shrink:0' })
                  : h('div', {
                      style: 'width:36px;height:36px;border-radius:50%;background:#10b981;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0'
                    }, senderName.slice(-1))

                ElNotification({
                  title: '',
                  duration: 10000,
                  position: 'bottom-right',
                  onClick: () => {
                    router.push('/chat')
                  },
                  message: h('div', {
                    style: 'display:flex;align-items:center;gap:10px;cursor:pointer'
                  }, [
                    avatarNode,
                    h('div', { style: 'min-width:0' }, [
                      h('div', { style: 'font-size:14px;font-weight:600;color:#1e293b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis' }, senderName),
                      h('div', { style: 'font-size:12px;color:#94a3b8;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:200px' }, contentPreview)
                    ])
                  ])
                })
              }
            }).catch(e => console.error('拉取会话详情失败:', e))
          } else {
            // 会话已存在，刷新详情
            chatApi.getConversationDetail(conversationId).then(apiConv => {
              const detail = transformConversation(apiConv)
              conv.name = detail.name || conv.name
              conv.type = detail.type
              conv.avatar = detail.avatar || conv.avatar
              conv.memberCount = detail.memberCount || conv.memberCount
              conv.ownerId = detail.ownerId || conv.ownerId
              conv.description = detail.description || conv.description
              conv._raw = detail._raw
              _saveToStorage()
              // 额外拉取成员列表
              _fetchAndApplyMembers(conversationId)

              // 更新未读数（如果不在当前会话）
              if (conversationId !== currentConversationId.value) {
                conv.unreadCount = (conv.unreadCount || 0) + 1
              }

              // 弹窗提示
              if (conversationId !== currentConversationId.value) {
                const senderName = data.appUserName || '微信用户'
                const contentPreview = message || '发起咨询'

                const avatarNode = conv.avatar
                  ? h('img', { src: conv.avatar, style: 'width:36px;height:36px;border-radius:50%;object-fit:cover;flex-shrink:0' })
                  : h('div', {
                      style: 'width:36px;height:36px;border-radius:50%;background:#10b981;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0'
                    }, senderName.slice(-1))

                ElNotification({
                  title: '',
                  duration: 10000,
                  position: 'bottom-right',
                  onClick: () => {
                    router.push('/chat')
                  },
                  message: h('div', {
                    style: 'display:flex;align-items:center;gap:10px;cursor:pointer'
                  }, [
                    avatarNode,
                    h('div', { style: 'min-width:0' }, [
                      h('div', { style: 'font-size:14px;font-weight:600;color:#1e293b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis' }, senderName),
                      h('div', { style: 'font-size:12px;color:#94a3b8;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:200px' }, contentPreview)
                    ])
                  ])
                })
              }
            }).catch(e => console.error('刷新会话详情失败:', e))
          }
          break
        }
      }

      if (conversationId) _saveToStorage()
    })

    // 被踢下线
    ws.on('kicked', (data) => {
      ElMessage.warning(data?.reason || '您的账号在其他设备登录')
      const userStore = useUserStore()
      userStore.clearUser()
      destroyIMWebSocket()
      window.location.href = '/login'
    })

    // 服务端错误
    ws.on('error', (data) => {
      console.error('[IM] 服务端错误:', data)
      if (data?.message) {
        ElMessage.error(data.message)
      }
    })

    ws.connect()
  }

  function disconnectWebSocket() {
    destroyIMWebSocket()
    wsConnected.value = false
    _wsInitialized = false
  }

  // 发送正在输入状态
  function sendTyping() {
    if (!currentConversationId.value) return
    const ws = getIMWebSocket()
    if (ws.state === 'AUTHENTICATED') {
      ws.send('typing', { conversationId: currentConversationId.value })
    }
  }

  // ========== 内部辅助方法 ==========

  function _getMemberName(conversationId, userId) {
    const conv = conversations.value.find(c => c.id === conversationId)
    if (!conv?.members) return ''
    const member = conv.members.find(m => String(m.id) === String(userId))
    return member?.name || ''
  }

  function _updateConversationLastMessage(convId, msg) {
    const conv = conversations.value.find(c => c.id === convId)
    if (!conv) return
    conv.lastMessage = formatLastMessage(msg)
    conv.lastMessageTime = msg.time
  }

  // ========== 导出 ==========
  return {
    // 状态
    conversations,
    currentConversationId,
    messages,
    loading,
    messageLoading,
    filterTab,
    searchKeyword,
    showInfoPanel,
    replyToMessage,
    messageSearchKeyword,
    messageSearchActive,
    showNewConversationDialog,
    availableContacts,
    messageHasMore,
    wsConnected,
    typingUsers,
    onlineUserIds,
    // 计算属性
    currentConversation,
    currentMessages,
    filteredConversations,
    totalUnreadCount,
    messageSearchResults,
    currentUserRole,
    // 会话管理
    loadConversations,
    selectConversation,
    // 消息管理
    loadMoreMessages,
    sendNewMessage,
    sendFileMessage,
    sendImageMessage,
    deleteMessage,
    // 回复与搜索
    setReplyTo,
    clearReplyTo,
    toggleInfoPanel,
    toggleMessageSearch,
    // 会话设置
    pinConversation,
    muteConversation,
    deleteConversation,
    isConversationPinned,
    isConversationMuted,
    // 联系人与新建
    loadContacts,
    createNewConversation,
    createNewGroup,
    // 群聊管理
    addGroupMembers,
    removeGroupMember,
    setMemberRole,
    dissolveGroup,
    leaveGroup,
    transferOwner,
    updateGroupInfo,
    // WebSocket
    initWebSocket,
    disconnectWebSocket,
    sendTyping,
    // 用户信息缓存
    getUserInfo,
    // 图片缓存
    getImageUrl
  }
})
